/**
 * Cart Attributes Management
 * Centralized utilities for managing Shopify cart attributes with proper locking,
 * retry logic, and error handling.
 */

const DEBUG = typeof window !== 'undefined' && (window.Shopify?.designMode || false);

/**
 * Debug logging wrapper
 * @param {string} prefix - Log prefix
 * @param  {...any} args - Arguments to log
 */
function debugLog(prefix, ...args) {
    if (DEBUG) {
        console.debug(`[${prefix}]`, ...args);
    }
}

/**
 * Configuration for cart operations
 */
const CONFIG = {
    MAX_RETRY_ATTEMPTS: 3,
    RETRY_DELAYS: [500, 1000, 2000], // Exponential backoff delays in ms
    MARKET_DATA_TIMEOUT: 5000, // 5 second timeout for market data
    LOCK_TIMEOUT: 10000, // 10 second lock timeout
    MAX_QUEUE_SIZE: 10 // Maximum queued requests
};

/**
 * Global state for cart attribute operations
 * Namespaced under window.shopifyMarketCart to avoid pollution
 */
if (typeof window !== 'undefined') {
    if (!window.shopifyMarketCart) {
        window.shopifyMarketCart = {
            currentCartMarket: {
                market: null,
                market_url: null
            },
            shopCountryRedirectOption: null,
            updateLock: {
                isLocked: false,
                queue: []
            }
        };
    }
}

/**
 * Updates cart attributes with retry logic and proper error handling
 * @param {Object} attributes - Cart attributes to update
 * @param {number} maxRetries - Maximum number of retry attempts
 * @returns {Promise<Object>} Updated cart data
 * @throws {Error} If all retry attempts fail
 */
export async function updateCartAttributes(attributes, maxRetries = CONFIG.MAX_RETRY_ATTEMPTS) {
    if (!attributes || typeof attributes !== 'object') {
        throw new Error('Invalid attributes object');
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            debugLog('Cart', `Update attempt ${attempt}/${maxRetries}`, attributes);

            const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({ attributes })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (!data || typeof data !== 'object') {
                throw new Error('Invalid response format from cart update');
            }

            debugLog('Cart', 'Update successful', data);
            return data;
        } catch (error) {
            console.warn(`[Cart] Update attempt ${attempt}/${maxRetries} failed:`, error.message);

            if (attempt === maxRetries) {
                console.error('[Cart] All retry attempts failed');
                throw error;
            }

            // Exponential backoff delay
            const delay = CONFIG.RETRY_DELAYS[attempt - 1] || 2000;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
}

/**
 * Updates market cart attributes with proper locking to prevent race conditions
 * @param {string} marketName - Market short name or full name
 * @param {string} marketUrl - Market redirect URL
 * @returns {Promise<Object>} Updated cart data
 */
export async function updateMarketAttribute(marketName, marketUrl) {
    if (!marketName || !marketUrl) {
        throw new Error('Market name and URL are required');
    }

    const state = window.shopifyMarketCart;

    // Implement queue-based locking to prevent concurrent updates
    return new Promise(async (resolve, reject) => {
        // Check queue size
        if (state.updateLock.queue.length >= CONFIG.MAX_QUEUE_SIZE) {
            reject(new Error('Market update queue full'));
            return;
        }

        // Add to queue if locked
        if (state.updateLock.isLocked) {
            debugLog('Market', 'Update locked, adding to queue');

            const timeoutId = setTimeout(() => {
                // Remove from queue on timeout
                const index = state.updateLock.queue.findIndex((item) => item.resolve === resolve);
                if (index !== -1) {
                    state.updateLock.queue.splice(index, 1);
                }
                reject(new Error('Market update lock timeout'));
            }, CONFIG.LOCK_TIMEOUT);

            state.updateLock.queue.push({
                marketName,
                marketUrl,
                resolve: (result) => {
                    clearTimeout(timeoutId);
                    resolve(result);
                },
                reject: (error) => {
                    clearTimeout(timeoutId);
                    reject(error);
                }
            });
            return;
        }

        // Acquire lock
        state.updateLock.isLocked = true;
        let lockTimeoutId = null;

        try {
            // Set lock timeout
            lockTimeoutId = setTimeout(() => {
                console.error('[Market] Lock held too long, forcing release');
                state.updateLock.isLocked = false;
                reject(new Error('Lock timeout'));
            }, CONFIG.LOCK_TIMEOUT);

            // Check if update is actually needed
            if (!needsMarketUpdate(state.currentCartMarket, marketName, marketUrl)) {
                debugLog('Market', 'Market already up to date:', marketName);
                clearTimeout(lockTimeoutId);
                resolve({ skipped: true });
                return;
            }

            debugLog('Market', 'Updating market attribute:', {
                from: state.currentCartMarket.market,
                to: marketName
            });

            const result = await updateCartAttributes({
                market: marketName,
                market_url: marketUrl
            });

            clearTimeout(lockTimeoutId);

            // Update local state
            state.currentCartMarket = {
                market: marketName,
                market_url: marketUrl
            };

            debugLog('Market', 'Market attribute updated successfully');
            resolve(result);
        } catch (error) {
            console.error('[Market] Failed to update market attribute:', error);
            if (lockTimeoutId) clearTimeout(lockTimeoutId);
            reject(error);
        } finally {
            // Release lock
            state.updateLock.isLocked = false;

            // Process next item in queue
            if (state.updateLock.queue.length > 0) {
                const next = state.updateLock.queue.shift();
                updateMarketAttribute(next.marketName, next.marketUrl)
                    .then(next.resolve)
                    .catch(next.reject);
            }
        }
    });
}

/**
 * Checks if market cart attribute needs updating
 * @param {Object} currentMarket - Current market state
 * @param {string} newMarketName - New market name
 * @param {string} newMarketUrl - New market URL
 * @returns {boolean} True if update is needed
 */
export function needsMarketUpdate(currentMarket, newMarketName, newMarketUrl) {
    return (
        !currentMarket?.market ||
        currentMarket.market !== newMarketName ||
        currentMarket.market_url !== newMarketUrl
    );
}

/**
 * Validates redirect option data structure
 * @param {Object} option - Redirect option to validate
 * @returns {Object} Validation result with valid flag and error message
 */
function validateRedirectOption(option) {
    if (!option || typeof option !== 'object') {
        return { valid: false, error: 'Invalid redirect option type' };
    }

    const required = ['redirect_url'];
    const missing = required.filter((field) => !option[field]);

    if (missing.length > 0) {
        return {
            valid: false,
            error: `Missing required fields: ${missing.join(', ')}`
        };
    }

    if (!option.short_name && !option.name) {
        return {
            valid: false,
            error: 'Must have either short_name or name'
        };
    }

    return { valid: true };
}

/**
 * Waits for market data to become available
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Object>} Shop country redirect option
 * @throws {Error} If timeout is reached or data is invalid
 */
export function waitForMarketData(timeout = CONFIG.MARKET_DATA_TIMEOUT) {
    return new Promise((resolve, reject) => {
        const state = window.shopifyMarketCart;

        // Already available - validate it
        if (state.shopCountryRedirectOption) {
            const validation = validateRedirectOption(state.shopCountryRedirectOption);
            if (!validation.valid) {
                reject(new Error(`Invalid market data: ${validation.error}`));
                return;
            }
            resolve(state.shopCountryRedirectOption);
            return;
        }

        let timeoutId;
        let eventHandled = false;

        const handleReady = (event) => {
            if (eventHandled) return;
            eventHandled = true;

            clearTimeout(timeoutId);
            document.removeEventListener('market:ready', handleReady);

            const redirectOption = event.detail?.shopCountryRedirectOption || state.shopCountryRedirectOption;

            if (!redirectOption) {
                reject(new Error('Market data received but empty'));
                return;
            }

            // Validate data structure
            const validation = validateRedirectOption(redirectOption);
            if (!validation.valid) {
                reject(new Error(`Invalid market data: ${validation.error}`));
                return;
            }

            resolve(redirectOption);
        };

        // Set up timeout
        timeoutId = setTimeout(() => {
            if (eventHandled) return;
            eventHandled = true;

            document.removeEventListener('market:ready', handleReady);
            reject(new Error(`Market data timeout after ${timeout}ms`));
        }, timeout);

        // Listen for market:ready event
        document.addEventListener('market:ready', handleReady);
    });
}

/**
 * Gets current market state
 * @returns {Object} Current cart market state
 */
export function getCurrentMarket() {
    return window.shopifyMarketCart?.currentCartMarket || { market: null, market_url: null };
}

/**
 * Gets the initial cart market state (set by Liquid inline in theme.liquid)
 * @returns {Object} Current cart market state
 */
export function getInitialMarketState() {
    return window.shopifyMarketCart?.currentCartMarket || { market: null, market_url: null };
}

/**
 * Ensures market attribute is updated when market data becomes available
 * Handles all market change scenarios: URL changes, modal accepts, cart adds
 * @returns {Promise<void>}
 */
export async function ensureMarketAttributeIsUpdated() {
    try {
        debugLog('Market', 'Ensuring market attribute is updated...');

        // Wait for market data to be available
        const redirectOption = await waitForMarketData();

        const { short_name, redirect_url, name } = redirectOption;

        if (!redirect_url) {
            throw new Error('Missing redirect_url in shopCountryRedirectOption');
        }

        const marketName = short_name || name;

        // Update if needed (lock is handled inside updateMarketAttribute)
        await updateMarketAttribute(marketName, redirect_url);
    } catch (error) {
        console.error('[Market] Failed to ensure market attribute updated:', error);
    }
}

// Expose functions globally for use in theme.liquid
if (typeof window !== 'undefined') {
    if (!window.shopifyMarketCart) {
        window.shopifyMarketCart = {
            currentCartMarket: { market: null, market_url: null },
            shopCountryRedirectOption: null,
            updateLock: { isLocked: false, queue: [] }
        };
    }
    window.shopifyMarketCart.ensureMarketAttributeIsUpdated = ensureMarketAttributeIsUpdated;
}
